import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { backfillPurchaseProjects, refreshFxRates, setFxRate } from '../../api/client'
import { formatNumber } from '../budget/format'

/**
 * Exchange rates and the two admin actions the budget depends on.
 *
 * Both actions exist because the budget can be silently wrong without them:
 * a currency with no rate leaves orders uncounted, and an order created before
 * the project link existed never reaches a budget at all.
 */
export default function FxRatesPanel({ rates, isAdmin, onChanged }) {
  const { t } = useTranslation('engineering')
  const [busy, setBusy] = useState('')
  const [message, setMessage] = useState(null)
  const [editing, setEditing] = useState(null)   // currency being typed in
  const [value, setValue] = useState('')

  const run = async (key, fn) => {
    setBusy(key)
    setMessage(null)
    try { return await fn() }
    finally { setBusy('') }
  }

  const handleRefresh = () => run('refresh', async () => {
    const res = await refreshFxRates()
    // Report per provider: with two of them, "it worked" and "it failed" are
    // both too coarse — one can answer while the other times out.
    const detail = (res.providers || [])
      .map(p => (p.ok ? `${p.provider}: ${p.count}` : `${p.provider}: ${p.error}`))
      .join('\n')
    setMessage({
      tone: res.ok ? 'ok' : 'bad',
      text: [res.ok ? t('budget.refreshed', { count: res.updated.length })
                    : t('budget.refreshFailed'), detail].filter(Boolean).join('\n'),
    })
    onChanged?.()
  })

  // Dry run first, always: the operator sees what would be linked and what
  // matches nothing before a single row is written.
  const handleBackfill = () => run('backfill', async () => {
    const dry = await backfillPurchaseProjects(false)
    const unmatched = dry.unmatched.map(u => `${u.reference} (${u.count})`).join(', ')
    const lines = [
      t('budget.backfillWouldLink', { count: dry.matched }),
      dry.skipped_ambiguous ? t('budget.backfillAmbiguous', { count: dry.skipped_ambiguous }) : '',
      unmatched ? `${t('budget.backfillUnmatched')}: ${unmatched}` : '',
    ].filter(Boolean).join('\n')

    if (dry.matched === 0) {
      setMessage({ tone: 'ok', text: lines })
      return
    }
    if (!confirm(`${lines}\n\n${t('budget.backfillConfirm')}`)) return
    const applied = await backfillPurchaseProjects(true)
    setMessage({ tone: 'ok', text: t('budget.backfillDone', { count: applied.matched }) })
    onChanged?.()
  })

  const handleSaveRate = async (currency) => {
    const number = Number(value)
    if (!Number.isFinite(number) || number <= 0) return
    await run('rate', async () => {
      await setFxRate({ currency, rate_clp: number })
      setEditing(null)
      setValue('')
      onChanged?.()
    })
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-gray-700">{t('budget.ratesTitle')}</p>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={handleRefresh} disabled={!!busy}
              title={t('budget.refreshHint')}
              className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              {busy === 'refresh' ? '…' : t('budget.refreshRates')}
            </button>
            <button onClick={handleBackfill} disabled={!!busy}
              title={t('budget.backfillHint')}
              className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              {busy === 'backfill' ? '…' : t('budget.backfill')}
            </button>
          </div>
        )}
      </div>

      {message && (
        <p className={`text-xs whitespace-pre-line px-3 py-2 rounded-lg ${
          message.tone === 'ok' ? 'bg-gray-50 text-gray-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </p>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {rates.map(r => (
          <div key={r.currency} className="flex items-center gap-1.5 tabular-nums">
            <span className="font-medium text-gray-700 w-8">{r.currency}</span>
            {editing === r.currency ? (
              <>
                <input autoFocus type="number" step="0.01" min="0" value={value}
                  onChange={e => setValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveRate(r.currency)
                    if (e.key === 'Escape') { setEditing(null); setValue('') }
                  }}
                  className="w-24 border border-gray-300 rounded px-1.5 py-0.5 text-xs" />
                <button onClick={() => handleSaveRate(r.currency)}
                  className="text-gray-900 underline">{t('budget.save')}</button>
              </>
            ) : (
              <>
                {r.missing
                  ? <span className="text-amber-700">{t('budget.rateMissing')}</span>
                  : <>
                      <span className="text-gray-700">{formatNumber(r.rate_clp)}</span>
                      <span className="text-gray-400">{r.rate_date}</span>
                      <span className={r.official ? 'text-emerald-700' : 'text-gray-400'}
                            title={t(r.official ? 'budget.sourceOfficialHint'
                                                : 'budget.sourceMarketHint')}>
                        {t(`budget.source_${r.source_label || 'manual'}`,
                           r.source_label || '')}
                      </span>
                      {r.stale && (
                        <span className="text-amber-700">
                          {t('budget.rateStale', { days: r.age_days })}
                        </span>
                      )}
                    </>}
                {isAdmin && (
                  <button
                    onClick={() => {
                      setEditing(r.currency)
                      setValue(r.rate_clp != null ? String(r.rate_clp) : '')
                    }}
                    className="text-gray-400 hover:text-gray-700">✎</button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">{t('budget.ratesLegend')}</p>
      {isAdmin && <p className="text-[11px] text-gray-400">{t('budget.ratesHint')}</p>}
    </div>
  )
}
